import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';
import { AuthState } from './types';
import { BLAdmin } from '@/types/user';
import { Channel, ChannelAuthIntegrationOnly } from '@/types/channel';
import { GetChannelsFromStoredSession, authCode } from '@/connections/auth-code';
import { GetAccountFromStoredSession } from '@/connections/create-account';
import { IRedirectOptions } from '@/types/redirect';
import { ForgotPassword } from '@/connections/forgot-password';
import { getStorageItem, removeStorageItem, setStorageItem } from '@/utils/storage/local-storage';
import { StoredTokens } from '@/utils/storage/types';
import { getUserFromToken, setStorageAndGetUser } from './utils';
import { RefreshToken, authLogin } from '@/connections/auth';
import { AuthConsumer } from '@/types/oauth';
import { GetChannels } from '@/connections/channels';
import { AdminAuthRequest, SSOAdminAuth } from '@/connections/sso';
import { jwtDecode } from '@/utils/jwt/jwt-decode';
import { ThemeName } from '@/types/theme';

export const reducers = (create: ReducerCreators<AuthState>) => ({
  setToken: create.reducer((state, action: PayloadAction<string | null>): void => {
    state.token = action.payload;
  }),
  setUser: create.reducer((state, action: PayloadAction<BLAdmin | null>): void => {
    state.user = action.payload;
  }),
  setChannels: create.reducer((state, action: PayloadAction<Channel[]>): void => {
    state.channels = action.payload;
  }),
  setSigningIn: create.reducer((state, action: PayloadAction<boolean>): void => {
    state.signingIn = action.payload;
  }),
  setSignInFailure: create.reducer((state, action: PayloadAction<string>): void => {
    state.signInFailure = action.payload;
  }),
  setUpdatePasswordLoading: create.reducer((state, action: PayloadAction<boolean>): void => {
    state.updatePasswordLoading = action.payload;
  }),
  setUpdatePasswordError: create.reducer((state, action: PayloadAction<string>): void => {
    state.updatePasswordError = action.payload;
  }),
  setUpdatePasswordSuccess: create.reducer((state, action: PayloadAction<boolean>): void => {
    state.updatePasswordSuccess = action.payload;
  }),
  setTempProfile: create.reducer((state, action: PayloadAction<string | null>): void => {
    state.temp_profile = action.payload;
  }),
  setTwoFactorStored: create.reducer((state, action: PayloadAction<string | null>): void => {
    state.two_factor_stored = action.payload;
  }),
  setPhoneHash: create.reducer((state, action: PayloadAction<string | null>): void => {
    state.phone_hash = action.payload;
  }),
  setRequestPhone: create.reducer((state, action: PayloadAction<boolean>): void => {
    state.request_phone = action.payload;
  }),
  setOnboardedLoading: create.reducer((state, action: PayloadAction<boolean>): void => {
    state.onboardedLoading = action.payload;
  }),
  setAwaitCode: create.reducer((state, action: PayloadAction<boolean>): void => {
    state.awaitCode = action.payload;
  }),
  setChangingChannels: create.reducer((state, action: PayloadAction<boolean>): void => {
    state.changingChannels = action.payload;
  }),
  setChannelSSO: create.reducer(
    (state, action: PayloadAction<Channel | Record<string, never> | ChannelAuthIntegrationOnly>): void => {
      state.channelSSO = action.payload;
    }
  ),
  getAdminChannels: create.asyncThunk(
    async () => {
      return GetChannels();
    },
    {
      fulfilled(state, action) {
        state.channels = action.payload;
      },
    }
  ),
  signIn: create.asyncThunk(
    async (
      {
        email,
        password,
        two_factor_stored,
        authConsumer,
        allowNoChannels = false,
        theme,
      }: {
        email: string;
        password: string;
        two_factor_stored: string | null;
        authConsumer: AuthConsumer;
        allowNoChannels: boolean;
        theme?: ThemeName;
      },
      { rejectWithValue }
    ) => {
      try {
        return authLogin(email, password, two_factor_stored, authConsumer, allowNoChannels, theme);
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      pending(state) {
        state.signingIn = true;
      },
      fulfilled(state, action) {
        if (action.payload.token) {
          if (action.payload.auth_consumer && action.payload.auth_consumer !== AuthConsumer.Platform) {
            state.user = getUserFromToken(action.payload.token);
            state.oauthFlowToken = action.payload.token;
            state.signInFailure = '';
          } else {
            state.user = setStorageAndGetUser(action.payload.token) as BLAdmin;
            state.token = action.payload.token;
            state.signInFailure = '';
          }
        } else if (action.payload.two_factor_required && action.payload.temp_profile) {
          state.temp_profile = action.payload.temp_profile;
          state.two_factor_stored = action.payload.temp_profile;
        } else {
          state.signInFailure = 'Failed to sign in.';
        }
        state.signingIn = false;
      },
      rejected(state, action) {
        if (action.payload instanceof Error) {
          state.signInFailure = action.payload.message;
        } else {
          state.signInFailure = 'Failed to sign in.';
        }
        state.signingIn = false;
      },
    }
  ),
  signOut: create.reducer((state): void => {
    removeStorageItem(StoredTokens.currentUser);
    state.token = null;
    state.user = null;
    state.channels = [];
    state.presenterAuthType = null;
  }),
  refreshToken: create.asyncThunk(
    async (_, { rejectWithValue }) => {
      try {
        return RefreshToken();
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled(state, action) {
        if (action.payload?.token) {
          state.user = setStorageAndGetUser(action.payload.token) as BLAdmin;
          state.token = action.payload.token;
          state.signInFailure = '';
        } else {
          state.user = null;
          state.token = null;
        }
      },
    }
  ),
  confirmCode: create.asyncThunk(
    async ({
      code,
      temp_profile,
      should_confirm,
      request_storage,
    }: {
      temp_profile: string;
      code: string;
      should_confirm: boolean;
      request_storage: boolean;
    }) => {
      return authCode(temp_profile, code, should_confirm, request_storage);
    },
    {
      pending(state) {
        state.signingIn = true;
      },
      fulfilled(state, action) {
        if (action.payload.token && action.payload.storage_token) {
          setStorageItem(StoredTokens.storedTwoFactor, action.payload.storage_token);
          const user = setStorageAndGetUser(action.payload.token) as BLAdmin;
          state.token = action.payload.token;
          state.user = user;
        } else {
          state.signInFailure = 'Failed to confirm code.';
        }
        state.signingIn = false;
      },
      rejected(state, action) {
        if (action.payload instanceof Error) {
          state.signInFailure = action.payload.message;
        } else {
          state.signInFailure = 'Failed to sign in.';
        }
      },
    }
  ),
  forgotPassword: create.asyncThunk(
    async (
      {
        email,
        hostname,
        redirectOptions,
      }: {
        email: string;
        hostname: string;
        redirectOptions?: IRedirectOptions;
      },
      { rejectWithValue }
    ) => {
      try {
        return ForgotPassword(email, hostname, redirectOptions);
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      pending(state) {
        state.forgotPasswordLoading = true;
        state.forgotPasswordError = '';
        state.forgotPasswordSuccess = false;
      },
      fulfilled(state) {
        state.forgotPasswordLoading = false;
        state.forgotPasswordSuccess = true;
      },
      rejected(state, action) {
        state.forgotPasswordLoading = false;
        state.forgotPasswordError = action.error.message ?? '';
      },
    }
  ),
  fetchChannelsInfo: create.asyncThunk(
    async (token: string) => {
      return GetChannelsFromStoredSession(token);
    },
    {
      pending(state) {
        state.channelsInfoLoading = true;
        state.channelsInfoFailed = false;
      },
      fulfilled(state, action) {
        state.channelsInfo = action.payload;
        state.channelsInfoLoading = false;
        state.channelsInfoFailed = false;
      },
      rejected(state) {
        state.channelsInfoLoading = false;
        state.channelsInfoFailed = true;
      },
    }
  ),
  fetchReducedUser: create.asyncThunk(
    async (token: string, { rejectWithValue }) => {
      try {
        return GetAccountFromStoredSession(token);
      } catch (e) {
        rejectWithValue(e);
      }
    },
    {
      pending(state) {
        state.gettingChannelsInfo = true;
      },
      fulfilled(state, action) {
        if (action.payload) {
          state.reduced_user = action.payload.account;
          state.gettingChannelsInfo = false;
        }
      },
      rejected(state, action) {
        state.gettingChannelsInfo = false;
        state.gettingChannelsInfoError = action.error.message ?? '';
      },
    }
  ),
  ssoAdminAuth: create.asyncThunk(
    async (params: AdminAuthRequest, { rejectWithValue }) => {
      try {
        return SSOAdminAuth(params);
      } catch (e) {
        rejectWithValue(e);
      }
    },
    {
      pending(state) {
        state.signingIn = true;
      },
      fulfilled(state, action) {
        if (action.payload?.token) {
          const user = jwtDecode(action.payload.token) as BLAdmin;
          const email = getStorageItem('user_email');

          if (email !== user.email) {
            state.authMismatch = { requested: email, actual: user.email };
          }

          state.user = user;
          state.token = action.payload.token;
          state.signedIn = true;
        } else {
          state.signInFailure = action.payload?.reason ?? 'unknown error';
        }
      },
      rejected(state, action) {
        state.signInFailure = action.error.message ?? '';
        state.signingIn = false;
      },
    }
  ),
});
